/**
 * OrganizationSetup Component
 * Shown when user has an organization tier but hasn't created an organization yet
 */
import { useState } from 'react';
import { organizationsApi, CreateOrganizationRequest } from '../../api/organizations';
import toast from 'react-hot-toast';

interface OrganizationSetupProps {
  onOrganizationCreated: () => void;
  userTier?: string;
}

export default function OrganizationSetup({ onOrganizationCreated, userTier = '' }: OrganizationSetupProps) {
  const [formData, setFormData] = useState<CreateOrganizationRequest>({
    name: '',
    slug: '',
    subscription_tier: userTier, // Auto-filled from user's tier
    email_domain_whitelist: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailDomain, setEmailDomain] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await organizationsApi.createOrganization(formData);
      toast.success('Organization created successfully!');
      onOrganizationCreated();
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      const errorMessage = error.response?.data?.detail?.message || 'Failed to create organization';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddEmailDomain = () => {
    if (emailDomain && !formData.email_domain_whitelist?.includes(emailDomain)) {
      setFormData({
        ...formData,
        email_domain_whitelist: [...(formData.email_domain_whitelist || []), emailDomain]
      });
      setEmailDomain('');
    }
  };

  const handleRemoveEmailDomain = (domain: string) => {
    setFormData({
      ...formData,
      email_domain_whitelist: formData.email_domain_whitelist?.filter(d => d !== domain) || []
    });
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    });
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Set Up Your Organization
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You have an organization subscription tier. Create your organization to start inviting team members.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Organization Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Organization Name *
            </label>
            <input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="My Church or Ministry"
            />
          </div>

          {/* Organization Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Organization URL Slug *
            </label>
            <input
              id="slug"
              type="text"
              required
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="my-church-or-ministry"
              pattern="[a-z0-9-]+"
              title="Only lowercase letters, numbers, and hyphens"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This will be used in invitation links. Only lowercase letters, numbers, and hyphens.
            </p>
          </div>

          {/* Email Domain Whitelist (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Domain Whitelist (Optional)
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Restrict invitations to specific email domains (e.g., yourchurch.org)
            </p>

            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={emailDomain}
                onChange={(e) => setEmailDomain(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="example.org"
              />
              <button
                type="button"
                onClick={handleAddEmailDomain}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Add
              </button>
            </div>

            {formData.email_domain_whitelist && formData.email_domain_whitelist.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.email_domain_whitelist.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                  >
                    {domain}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmailDomain(domain)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.slug}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
